import { useState, useEffect } from "react";
import {
  useCreateSize,
  useOneSize,
  useUpdateSize,
} from "http-hooks/size/useSize";
import { useAllUnits } from "http-hooks/unit/useUnit";
import { Switch } from "@headlessui/react";
import Select from "components/ui/Select";
import Input from "components/ui/Input";
import Modal from "components/ui/modals";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function SetSize({ id, onClose, open }) {
  const [sizeDetails, setSizeDetails] = useState({
    itmsz: "",
    umid: "",
    ag_status: 1,
  });
  const [selectedUnit, setSelectedUnit] = useState(null);

  const { isLoading: isLoadingSize, data } = useOneSize(id);
  const { isLoading: isAllUnitsLoading, data: { data: allunits } = [] } = useAllUnits({
    unitname: "",
    systemg: "",
  });

  const unitIdOfSizesIn = allunits?.find((unit) => unit.unitname === "sizes-in")?.unitId;
  const unitOptions = allunits
    ?.filter((unit) => unit.unitidunder === unitIdOfSizesIn)
    ?.map((unit) => ({ value: unit.unitname, label: unit.unitname, ...unit })) || [];

  const { mutateAsync: mutateCreate, isPending: isCreating } = useCreateSize();
  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useUpdateSize();

  const isPending = isCreating || isUpdating;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSizeDetails((prevData) => ({ ...prevData, [name]: value }));
  };

  useEffect(() => {
    if (data) {
      setSizeDetails((prev) => {
        const updatedDetails = {};
        Object.keys(prev).forEach((key) => {
          updatedDetails[key] = data[key] ?? prev[key];
        });
        return updatedDetails;
      });
      if (data.umid) {
        const unit = allunits?.find((unit) => unit.unitname === data.umid);
        if (unit) setSelectedUnit({ value: unit.unitname, label: unit.unitname, ...unit });
      }
    }
  }, [data, allunits]);

  useEffect(() => {
    if (selectedUnit) {
      setSizeDetails((prev) => ({ ...prev, umid: selectedUnit.value }));
    }
  }, [selectedUnit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (id) {
        await mutateUpdate({ id, data: sizeDetails });
      } else {
        await mutateCreate(sizeDetails);
      }
      onClose();
    } catch (error) {
      console.error("Operation failed:", error);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={(val) => !val && onClose()}
      header={
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {id ? "Update" : "Add"} Size
        </h3>
      }
      footer={
        <div className="flex justify-end gap-x-3">
          <button
            type="button"
            className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="set-size-form"
            disabled={isPending}
            className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Saving..." : "Save"}
          </button>
        </div>
      }
    >
      {id && isLoadingSize ? (
        <div className="animate-pulse space-y-6">
          <div className="h-9 bg-gray-200 rounded w-full" />
          <div className="h-9 bg-gray-200 rounded w-full" />
          <div className="h-6 bg-gray-200 rounded w-12" />
        </div>
      ) : (
        <form id="set-size-form" onSubmit={handleSubmit} className="space-y-6">
          <Input
            type="text"
            name="itmsz"
            id="itmsz"
            label="Item Size"
            maxLength="10"
            value={sizeDetails.itmsz}
            onChange={handleInputChange}
          />
          <Select
            options={unitOptions}
            value={selectedUnit}
            onChange={setSelectedUnit}
            placeholder="Select Unit Under"
            label="Unit Under"
            isLoading={isAllUnitsLoading}
          />
          {id && (
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Status
              </label>
              <Switch
                checked={!!sizeDetails.ag_status}
                onChange={(e) => setSizeDetails((prev) => ({ ...prev, ag_status: e ? 1 : 0 }))}
                className={classNames(
                  sizeDetails.ag_status ? "bg-indigo-600" : "bg-gray-200",
                  "relative inline-flex ml-1 mt-3 h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2"
                )}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    sizeDetails.ag_status ? "translate-x-5" : "translate-x-0",
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                  )}
                />
              </Switch>
            </div>
          )}
        </form>
      )}
    </Modal>
  );
}
